<template>
  <div 
    class="group bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-3 sm:p-4 cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
    :class="{ 'relative z-50': showCallMenu }"
    @click="handleCardClick"
  >
    <div class="flex items-start gap-3">
      <!-- Photo with Presence -->
      <div class="relative flex-shrink-0">
        <img
          v-if="photoUrl"
          :src="photoUrl"
          :alt="contact.display_name"
          class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl object-cover ring-2 ring-white/50 dark:ring-slate-700/50"
        >
        <div
          v-else
          class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br flex items-center justify-center shadow-inner"
          :class="avatarGradient"
        >
          <span class="text-sm sm:text-base font-semibold text-white">{{ initials }}</span>
        </div>
        
        <!-- Presence Indicator -->
        <div
          v-if="contact.entra_id"
          class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-slate-800 shadow-sm"
          :class="presenceClass"
          :title="presenceLabel"
        ></div>
      </div>

      <!-- Info -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5">
          <h3 class="font-semibold text-slate-900 dark:text-slate-100 truncate">
            {{ contact.display_name }}
          </h3>
          <!-- Favorite Star (visible when favorited) -->
          <Star 
            v-if="isFav" 
            class="w-4 h-4 text-amber-400 fill-amber-400 flex-shrink-0" 
          />
        </div>
        <p v-if="contact.job_title" class="text-sm text-slate-600 dark:text-slate-400 truncate">{{ contact.job_title }}</p>
        <p v-if="contact.company_name" class="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">
          {{ contact.company_name }}
        </p>
      </div>

      <!-- Favorite Button (top right) -->
      <button
        @click.stop="toggleFav"
        class="p-1 rounded transition-colors"
        :class="isFav 
          ? 'text-amber-400 hover:text-amber-500' 
          : 'text-slate-300 hover:text-amber-400 opacity-0 group-hover:opacity-100'"
        :title="isFav ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'"
      >
        <Star class="w-4 h-4" :class="isFav ? 'fill-amber-400' : ''" />
      </button>
    </div>

    <!-- Quick Actions -->
    <div class="flex justify-between items-center mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
      <div class="flex gap-1.5">
        <button
          v-if="hasValidEmail"
          @click.stop="openTeamsChat"
          class="p-2 bg-blue-100/80 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/60 rounded-xl transition-colors"
          title="Teams Nachricht"
        >
          <MessageSquare class="w-4 h-4" />
        </button>
        
        <a
          v-if="hasValidEmail"
          :href="`mailto:${contact.email}`"
          @click.stop
          class="p-2 bg-orange-100/80 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/60 rounded-xl transition-colors"
          title="E-Mail senden"
        >
          <Mail class="w-4 h-4" />
        </a>
        
        <!-- Call Button with Dropdown -->
        <div v-if="hasAnyCallOption" class="relative">
          <button
            @click.stop="showCallMenu = !showCallMenu"
            class="flex items-center gap-0.5 p-2 bg-green-100/80 dark:bg-green-900/40 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/60 rounded-xl transition-colors"
            title="Anrufen"
          >
            <Phone class="w-4 h-4" />
            <ChevronDown class="w-3 h-3" />
          </button>
          
          <!-- Call Menu Dropdown -->
          <div 
            v-if="showCallMenu"
            class="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1 min-w-[180px]"
            @click.stop
          >
            <!-- Teams Audio Call -->
            <button
              v-if="hasValidEmail"
              @click="openTeamsAudioCall"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <PhoneCall class="w-4 h-4 text-purple-500" />
              <span>Teams Anruf</span>
            </button>
            
            <!-- Business Phone -->
            <button
              v-if="businessPhone"
              @click="callNumber(businessPhone)"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <Phone class="w-4 h-4 text-green-500" />
              <div class="flex flex-col items-start">
                <span>Telefon</span>
                <span class="text-xs text-slate-400">{{ businessPhone }}</span>
              </div>
            </button>
            
            <!-- Mobile Phone -->
            <button
              v-if="mobilePhone"
              @click="callNumber(mobilePhone)"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <Phone class="w-4 h-4 text-blue-500" />
              <div class="flex flex-col items-start">
                <span>Mobil</span>
                <span class="text-xs text-slate-400">{{ mobilePhone }}</span>
              </div>
            </button>
          </div>
        </div>
        
        <button
          v-if="hasValidEmail"
          @click.stop="openTeamsVideoCall"
          class="p-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-md transition-colors"
          title="Teams Videoanruf"
        >
          <Video class="w-4 h-4" />
        </button>
      </div>

      <!-- Add to Contacts Button (for non-personal contacts) -->
      <button
        v-if="hasValidEmail && !contact.is_personal_contact"
        @click.stop="addToMyContacts"
        :disabled="isAddingContact || addedToContacts"
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-xl transition-colors"
        :class="addContactError
          ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
          : addedToContacts 
            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' 
            : 'text-slate-500 hover:text-blue-600 hover:bg-blue-100/80 dark:hover:text-blue-400 dark:hover:bg-blue-900/40'"
        :title="addContactError ? 'Fehler beim Hinzufügen' : addedToContacts ? 'Bereits hinzugefügt' : 'Zu meinen Kontakten hinzufügen'"
      >
        <CheckCircle v-if="addedToContacts" class="w-3.5 h-3.5" />
        <Loader2 v-else-if="isAddingContact" class="w-3.5 h-3.5 animate-spin" />
        <svg v-else-if="addContactError" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        <UserPlus v-else class="w-3.5 h-3.5" />
        <span v-if="addContactError" class="hidden sm:inline">Fehler</span>
        <span v-else-if="!addedToContacts && !isAddingContact" class="hidden sm:inline">Speichern</span>
        <span v-else-if="addedToContacts" class="hidden sm:inline">Gespeichert</span>
      </button>

      <!-- Delete from My Contacts Button (for personal contacts only) -->
      <button
        v-if="contact.is_personal_contact && contact.id"
        @click.stop="askDeleteConfirmation"
        :disabled="isDeletingContact"
        class="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30"
        title="Aus meinen Kontakten entfernen"
      >
        <Loader2 v-if="isDeletingContact" class="w-3.5 h-3.5 animate-spin" />
        <Trash2 v-else class="w-3.5 h-3.5" />
        <span class="hidden sm:inline">Entfernen</span>
      </button>
    </div>

    <!-- Delete Confirmation Modal (Teams doesn't support native confirm()) -->
    <Teleport to="body">
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="cancelDelete"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        
        <!-- Modal -->
        <div class="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in-95 duration-200">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
              <Trash2 class="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Kontakt entfernen?</h3>
          </div>
          
          <p class="text-slate-600 dark:text-slate-400 mb-6">
            Möchtest du <strong class="text-slate-900 dark:text-slate-100">{{ contact.display_name }}</strong> wirklich aus deinen Kontakten entfernen?
          </p>
          
          <div class="flex gap-3 justify-end">
            <button
              @click="cancelDelete"
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              @click="confirmDelete"
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              Entfernen
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { MessageSquare, Phone, Video, UserPlus, CheckCircle, Loader2, Star, Trash2, PhoneCall, ChevronDown, Mail } from 'lucide-vue-next';
import { fetchContactPhoto, getPresenceClass, getPresenceLabel, addContactToOutlook, deleteContactFromOutlook, myContacts, fetchMyContacts } from '../websocketService';
import { toggleFavorite, isFavorite, addToRecent, favorites } from '../localStorageService';
import { makePhoneCall } from '../callService';

const props = defineProps({
  contact: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['click']);

const photoUrl = ref(null);
const isAddingContact = ref(false);
const addContactError = ref(false); // Error state for add contact
const isDeletingContact = ref(false);
const showCallMenu = ref(false);
const showDeleteConfirm = ref(false); // Modal for delete confirmation (Teams doesn't support confirm())

// Check if contact is already saved in My Contacts (by email)
const isAlreadySaved = computed(() => {
  if (!props.contact.email) return false;
  const emailLower = props.contact.email.toLowerCase();
  return myContacts.value.some(c => {
    // Check primary email
    if (c.email && c.email.toLowerCase() === emailLower) return true;
    // Also check display_name as fallback (Outlook contacts might have different email)
    if (c.display_name && c.display_name.toLowerCase() === props.contact.display_name?.toLowerCase()) return true;
    return false;
  });
});

// Combined state: either just added in this session OR already in myContacts
const addedToContacts = computed(() => justAddedThisSession.value || isAlreadySaved.value);
const justAddedThisSession = ref(false);

// Favorites functionality
const isFav = ref(isFavorite(props.contact.email));

// Watch for external changes to favorites (e.g., from another component)
watch(favorites, () => {
  isFav.value = isFavorite(props.contact.email);
}, { deep: true });

const toggleFav = () => {
  toggleFavorite(props.contact.email);
  isFav.value = isFavorite(props.contact.email);
};

// Handle card click - track in recent and emit event
const handleCardClick = () => {
  addToRecent(props.contact);
  emit('click');
};

const initials = computed(() => {
  const name = props.contact.display_name || '';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?';
});

const avatarGradient = computed(() => {
  const palette = [
    'from-blue-400 to-blue-600',
    'from-violet-400 to-purple-600',
    'from-emerald-400 to-green-600',
    'from-orange-400 to-orange-600',
    'from-pink-400 to-rose-600',
    'from-teal-400 to-cyan-600',
    'from-indigo-400 to-indigo-700',
    'from-amber-400 to-orange-500',
    'from-red-400 to-rose-600',
    'from-sky-400 to-blue-500',
  ];
  const seed = (props.contact.display_name || props.contact.email || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return palette[seed % palette.length];
});

// Check if email is a real email (not a generated phone-based one)
const hasValidEmail = computed(() => {
  const email = props.contact.email;
  return email && !email.endsWith('@mailbox-contact.local');
});

// Check if contact has a phone number
const hasPhone = computed(() => {
  const phones = props.contact.business_phones;
  const phoneArray = typeof phones === 'string' ? JSON.parse(phones || '[]') : (phones || []);
  return props.contact.mobile_phone || phoneArray.length > 0;
});

// Check if any call option is available (Teams, Phone, or Mobile)
const hasAnyCallOption = computed(() => {
  return hasValidEmail.value || hasPhone.value;
});

// Get mobile phone
const mobilePhone = computed(() => props.contact.mobile_phone || null);

// Get business phone (first one)
const businessPhone = computed(() => {
  const phones = props.contact.business_phones;
  const phoneArray = typeof phones === 'string' ? JSON.parse(phones || '[]') : (phones || []);
  return phoneArray[0] || null;
});

// Get primary phone number
const primaryPhone = computed(() => {
  if (props.contact.mobile_phone) return props.contact.mobile_phone;
  const phones = props.contact.business_phones;
  const phoneArray = typeof phones === 'string' ? JSON.parse(phones || '[]') : (phones || []);
  return phoneArray[0] || null;
});

const presenceClass = computed(() => getPresenceClass(props.contact.entra_id));
const presenceLabel = computed(() => getPresenceLabel(props.contact.entra_id));

onMounted(async () => {
  // Only fetch photos for Entra users with an entra_id
  if (props.contact.is_entra_user && props.contact.entra_id && props.contact.email) {
    photoUrl.value = await fetchContactPhoto(props.contact.email, props.contact.entra_id);
  }
  
  // Close call menu on outside click
  document.addEventListener('click', closeCallMenu);
});

onUnmounted(() => {
  document.removeEventListener('click', closeCallMenu);
});

const closeCallMenu = () => {
  showCallMenu.value = false;
};

// Teams Deep Links
const openTeamsChat = () => {
  const url = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(props.contact.email)}`;
  window.open(url, '_blank');
};

// Call specific number
const callNumber = (number) => {
  showCallMenu.value = false;
  makePhoneCall(number);
};

// Teams Audio Call (without video)
const openTeamsAudioCall = () => {
  showCallMenu.value = false;
  const url = `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(props.contact.email)}`;
  window.open(url, '_blank');
};

const openTeamsVideoCall = () => {
  const url = `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(props.contact.email)}&withVideo=true`;
  window.open(url, '_blank');
};

// Add contact to user's Outlook
const addToMyContacts = async () => {
  if (isAddingContact.value || addedToContacts.value) return;
  
  isAddingContact.value = true;
  addContactError.value = false;
  try {
    await addContactToOutlook(props.contact);
    justAddedThisSession.value = true;
    // Refresh myContacts list to update the global state
    await fetchMyContacts();
  } catch (error) {
    console.error('Failed to add contact:', error);
    addContactError.value = true;
    // Reset error state after 3 seconds
    setTimeout(() => { addContactError.value = false; }, 3000);
  } finally {
    isAddingContact.value = false;
  }
};

// Show delete confirmation modal (Teams doesn't support native confirm())
const askDeleteConfirmation = () => {
  if (isDeletingContact.value) return;
  showDeleteConfirm.value = true;
};

// Cancel delete
const cancelDelete = () => {
  showDeleteConfirm.value = false;
};

// Delete contact from user's Outlook
const confirmDelete = async () => {
  showDeleteConfirm.value = false;
  isDeletingContact.value = true;
  try {
    await deleteContactFromOutlook(props.contact.id);
  } catch (error) {
    console.error('Failed to delete contact:', error);
    // Use a toast or inline message instead of alert() for Teams compatibility
  } finally {
    isDeletingContact.value = false;
  }
};
</script>
